import { format, parseISO } from 'date-fns';
import type { TripCard, TripDay } from '../redux/trip/trip.types';

export const formatPhoneNumberForLink = (phoneNumber: string) => phoneNumber.replace(/-|\s+/g, '');

/**
 * Returns true if the card is open on the target day (or has no opening hours data).
 * Used by drag-and-drop handlers when a card is dropped onto an itinerary day.
 */
export function isOpenOnDate(card: TripCard, day: TripDay): boolean {
  if (!card.openingHours) {
    return true;
  }
  const dayOfWeek = format(parseISO(day.dateISO), 'EEEE');
  const match = card.openingHours.find((h) => h.includes(dayOfWeek));
  return !match || !/closed/i.test(match);
}

export enum EAddressComponentsType {
  country = 'country',
  postal_code = 'postal_code',
  street_number = 'street_number',
  route = 'route',
  locality = 'locality',
  sublocality = 'sublocality',
}

export const getOpeningHours = (openingHours: string[]) =>
  openingHours.map((entry) => {
    const day = entry.substring(0, 3);
    const ranges = entry.substring(entry.indexOf(':') + 2).split(',');
    return { [day]: ranges };
  });

// Ordered Mon–Sun to match Google's weekday_text array (offset from JS's Sun=0)
const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

// Convenience constant for midnight boundary checks
const MINS_IN_DAY = 24 * 60;

// Parses a time string in 12-hour ("9:00 AM", "11 PM") or 24-hour ("18:00", "09:00") format
// into minutes since midnight. Returns null if the string is unparseable.
const parseTimeToMinutes = (t: string): number | null => {
  const s = t.trim();
  // 12-hour format: requires AM/PM suffix
  const m12 = s.match(/^(\d+)(?::(\d+))?\s*(AM|PM)$/i);
  if (m12) {
    let h = Number(m12[1]);
    const min = m12[2] ? Number(m12[2]) : 0;
    if (m12[3].toUpperCase() === 'AM' && h === 12) {
      h = 0;
    }
    if (m12[3].toUpperCase() === 'PM' && h !== 12) {
      h += 12;
    }
    return h * 60 + min;
  }
  // 24-hour format: must have colon (e.g. "18:00") to avoid ambiguity with bare numbers
  const m24 = s.match(/^(\d{1,2}):(\d{2})$/);
  if (m24) {
    const h = Number(m24[1]);
    const min = Number(m24[2]);
    if (h > 23 || min > 59) {
      return null;
    }
    return h * 60 + min;
  }
  return null;
};

// Formats minutes since midnight into a readable time string e.g. "9 AM", "1:30 PM"
const formatTime = (mins: number): string => {
  const h = Math.floor(mins / 60) % 24;
  const m = mins % 60;
  const period = h >= 12 ? 'PM' : 'AM';
  const hour = h % 12 === 0 ? 12 : h % 12;
  return m === 0 ? `${hour} ${period}` : `${hour}:${String(m).padStart(2, '0')} ${period}`;
};

// Given an opening time string without AM/PM and the closing time string, infers
// the correct period for the opening time. Handles formats like "12:00 – 10:00 PM".
const inferOpenPeriod = (openStr: string, closeStr: string): number | null => {
  const closePeriod = closeStr
    .trim()
    .match(/(AM|PM)$/i)?.[1]
    ?.toUpperCase();
  if (!closePeriod) {
    return null;
  }
  const closeMins = parseTimeToMinutes(closeStr);
  if (closeMins === null) {
    return null;
  }
  const samePeriodMins = parseTimeToMinutes(`${openStr.trim()} ${closePeriod}`);
  if (samePeriodMins === null) {
    return null;
  }
  // If open < close in the same period, the period is correct
  if (samePeriodMins <= closeMins) {
    return samePeriodMins;
  }
  // Otherwise try the opposite period (e.g. "9:00" with close "5:00 PM" → 9 AM)
  const otherPeriod = closePeriod === 'PM' ? 'AM' : 'PM';
  return parseTimeToMinutes(`${openStr.trim()} ${otherPeriod}`) ?? samePeriodMins;
};

// Extracts the opening time from the first parseable range in a day's hours list.
// Handles formats where the opening time lacks AM/PM (e.g. "12:00 – 10:00 PM").
const extractFirstOpenTime = (ranges: string[]): string | null => {
  for (const range of ranges) {
    const parts = range.split(/[–-]/);
    const openStr = parts[0];
    const closeStr = parts[1] ?? '';
    const mins = parseTimeToMinutes(openStr) ?? inferOpenPeriod(openStr, closeStr);
    if (mins !== null) {
      return formatTime(mins);
    }
  }
  return null;
};

// Returns true when the only entry is the string "Closed"
const isClosedAllDay = (ranges: string[]) =>
  ranges.length === 1 && ranges[0].trim().toLowerCase() === 'closed';

// Returns true when the place is open 24 hours (Google returns "Open 24 hours")
const isOpen24Hours = (ranges: string[]) =>
  ranges.length === 1 && ranges[0].trim().toLowerCase() === 'open 24 hours';

// Scans forward up to 7 days from `fromIdx` to find the next open day's opening time
const findNextOpenTime = (dayMap: Map<string, string[]>, fromIdx: number): string | null => {
  for (let i = 1; i <= 7; i++) {
    const ranges = dayMap.get(DAYS[(fromIdx + i) % 7]);
    if (!ranges || isClosedAllDay(ranges)) {
      continue;
    }
    const time = extractFirstOpenTime(ranges);
    if (time !== null) {
      return time;
    }
  }
  return null;
};

// Discriminated union: open carries a display label, closed carries the next open time (or null if unknown)
export type OpenStatus =
  | { isOpen: true; label: string }
  | { isOpen: false; nextOpenTime: string | null }
  | null;

// Returns the current open/closed status for a place based on its stored opening hours
export const getOpenStatus = (openingHours: string[] | undefined): OpenStatus => {
  if (!openingHours?.length) {
    return null;
  }

  // Build a day→ranges map for O(1) lookup instead of O(n) array search.
  // Parses "Monday: 9:00 AM – 10:00 PM, ..." format directly from the stored strings.
  const dayMap = new Map<string, string[]>(
    openingHours.map((entry) => {
      const day = entry.substring(0, 3);
      const ranges = entry.substring(entry.indexOf(':') + 2).split(',');
      return [day, ranges];
    })
  );

  const now = new Date();
  const todayIdx = (now.getDay() + 6) % 7; // JS Sun=0 → Mon=0
  const nowMins = now.getHours() * 60 + now.getMinutes();

  const ranges = dayMap.get(DAYS[todayIdx]);
  if (!ranges) {
    return null;
  }

  if (isClosedAllDay(ranges)) {
    return { isOpen: false, nextOpenTime: findNextOpenTime(dayMap, todayIdx) };
  }

  if (isOpen24Hours(ranges)) {
    return { isOpen: true, label: 'Open 24hrs' };
  }

  for (const range of ranges) {
    const [openStr, closeStr] = range.split('–');
    let openMins = parseTimeToMinutes(openStr);
    let closeMins = parseTimeToMinutes(closeStr);
    // Handle opening times that lack AM/PM (e.g. "12:00 – 10:00 PM")
    if (openMins === null && closeStr) {
      openMins = inferOpenPeriod(openStr, closeStr);
    }
    if (openMins === null || closeMins === null) {
      continue;
    }
    if (closeMins <= openMins) {
      closeMins += MINS_IN_DAY;
    } // overnight wrap
    // Shift now forward for overnight ranges so the comparison stays on a single timeline
    const effectiveNow =
      nowMins < openMins && closeMins > MINS_IN_DAY ? nowMins + MINS_IN_DAY : nowMins;
    if (effectiveNow >= openMins && effectiveNow < closeMins) {
      return { isOpen: true, label: `Open until ${formatTime(closeMins)}` };
    }
    if (effectiveNow < openMins) {
      return { isOpen: false, nextOpenTime: formatTime(openMins) };
    }
  }

  // Past all closing times for today — find the next open day
  return { isOpen: false, nextOpenTime: findNextOpenTime(dayMap, todayIdx) };
};

export const getLocalityFromAdrString = (
  addressComponents: google.maps.GeocoderAddressComponent[],
  formattedAddress: string
) => {
  let addressString = formattedAddress;

  for (const item of addressComponents) {
    if (
      item.types.includes(EAddressComponentsType.country) ||
      item.types.includes(EAddressComponentsType.postal_code) ||
      item.types.includes(EAddressComponentsType.street_number) ||
      item.types.includes(EAddressComponentsType.route)
    ) {
      addressString = addressString?.replace(item.long_name, '');
    }
  }
  const result = addressString
    .trim()
    .replace(/\d/g, '')
    .split(',')
    .filter((el: string) => Boolean(el.trim()) && el)
    .map((el) => el.trim())
    .at(-1);
  return result;
};

export const getLocalityOrSublocality = ({
  addressComponents,
  formattedAddress,
}: {
  addressComponents?: google.maps.GeocoderAddressComponent[];
  formattedAddress?: string | undefined;
}) => {
  if (!addressComponents) {
    return undefined;
  }

  let sublocality: string | undefined;
  for (const addressComponent of addressComponents) {
    if (addressComponent.types.find((type) => type === 'locality')) {
      return addressComponent.long_name;
    }
    if (addressComponent.types.find((type) => type === 'sublocality')) {
      return addressComponent.long_name;
    }
  }
  if (formattedAddress) {
    sublocality = getLocalityFromAdrString(addressComponents, formattedAddress);
  }
  return sublocality;
};
